import React, {
	createContext,
	type Dispatch,
	type SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import noop from 'lodash/noop';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import type { FieldErrors } from 'rest/request-create';
import type { RequestCreateKBState as RequestCreateKnowledgeBaseState } from 'state/persisted/request-create';
import type { TemplateForm } from '@atlassian/proforma-common-core/form';
import { ValidationFieldSet } from '@atlassian/proforma-form-renderer/field-validation';

import type { Forms } from '../../common/types/forms';
import { type MediaConfig } from '../../common/types/media';
import type { RegisterProformaForm } from '../../common/types/registration';
import type {
	JiraFieldData,
	ProformaRequestCreateContextProviderProps,
	SetMediaConfig,
} from './types';

/**
 * JSDCLOUD-19007: keep the media upload config in sync with the refreshed token from the store.
 * Previously the context kept the token from first render, which expired while the form was open.
 */
export const MEDIA_TOKEN_SYNC_GATE = 'proforma-jsdcloud-19007-media-token-sync';

const isMediaTokenSyncEnabled = (): boolean => FeatureGates.checkGate(MEDIA_TOKEN_SYNC_GATE);

const INITIAL_MEDIA_CONFIG: MediaConfig = {
	baseUrl: '',
	clientId: '',
	token: '',
	updateToken: noop,
};

const INITIAL_SET_MEDIA_CONFIG: SetMediaConfig = {
	setMediaUploadConfig: noop,
};

const LocaleContext = createContext<string>('en_US');
const PortalIdContext = createContext<number>(0);
const RequestTypeIdContext = createContext<number>(0);
const ServiceDeskIdContext = createContext<number>(0);
const ProjectIdContext = createContext<number>(0);
const MediaUploadConfigContext = createContext<MediaConfig>(INITIAL_MEDIA_CONFIG);
const SetMediaConfigContext = createContext<SetMediaConfig>(INITIAL_SET_MEDIA_CONFIG);
const ProformaTemplateFormContext = createContext<TemplateForm | undefined>(undefined);
const ReporterAccountIdContext = createContext<string | undefined>(undefined);
const KnowledgeBaseContext = createContext<RequestCreateKnowledgeBaseState | undefined>(undefined);
const BlockFormSubmissionContext = createContext<(blocking: boolean) => void>(noop);
const FieldErrorsContext = createContext<FieldErrors | undefined>(undefined);
const RegisterProformaFormContext = createContext<RegisterProformaForm | undefined>(undefined);
const SetProformaFormDirtyContext = createContext<
	((payload: { portalId: number; requestTypeId: number; isDirty: boolean }) => void) | undefined
>(undefined);
const ProformaFormIsDirtyContext = createContext<boolean>(false);
const JiraFieldDataContext = createContext<JiraFieldData>(new Map());
const SetJiraFieldDataContext = createContext<Dispatch<SetStateAction<JiraFieldData>>>(noop);
const FormsContext = createContext<Forms | undefined>(undefined);
const SetFormsContext = createContext<Dispatch<SetStateAction<Forms | undefined>>>(noop);
const ValidationFieldSetContext = createContext<ValidationFieldSet>(
	ValidationFieldSet.MODIFIED_FIELDS,
);
const SetValidationFieldSetContext =
	createContext<Dispatch<SetStateAction<ValidationFieldSet>>>(noop);

export const ProformaRequestCreateContextProvider = ({
	children,
	locale,
	portalId,
	requestTypeId,
	serviceDeskId,
	projectId,
	initialMediaUploadConfig,
	proformaTemplateForm,
	reporterAccountId,
	knowledgeBase,
	blockFormSubmission,
	fieldErrors,
	registerProformaForm,
	proformaFormIsDirty,
	setProformaFormDirtyAction,
}: ProformaRequestCreateContextProviderProps) => {
	const [mediaUploadConfig, setMediaUploadConfig] = useState<MediaConfig>(initialMediaUploadConfig);

	// Keep the context in sync with refreshed media credentials from props (JSDCLOUD-19007)
	useEffect(() => {
		if (isMediaTokenSyncEnabled()) {
			setMediaUploadConfig(initialMediaUploadConfig);
		}
	}, [initialMediaUploadConfig]);
	const setMediaConfig: SetMediaConfig = useMemo(
		() => ({
			setMediaUploadConfig,
		}),
		[setMediaUploadConfig],
	);
	const [jiraFieldData, setJiraFieldData] = useState<JiraFieldData>(new Map());
	const [forms, setForms] = useState<Forms | undefined>(undefined);
	const [validationFieldSet, setValidationFieldSet] = useState<ValidationFieldSet>(
		ValidationFieldSet.MODIFIED_FIELDS,
	);

	return (
		<LocaleContext.Provider value={locale}>
			<PortalIdContext.Provider value={portalId}>
				<RequestTypeIdContext.Provider value={requestTypeId}>
					<ServiceDeskIdContext.Provider value={serviceDeskId}>
						<ProjectIdContext.Provider value={projectId}>
							<MediaUploadConfigContext.Provider value={mediaUploadConfig}>
								<SetMediaConfigContext.Provider value={setMediaConfig}>
									<ProformaTemplateFormContext.Provider value={proformaTemplateForm}>
										<ReporterAccountIdContext.Provider value={reporterAccountId}>
											<KnowledgeBaseContext.Provider value={knowledgeBase}>
												<BlockFormSubmissionContext.Provider value={blockFormSubmission}>
													<FieldErrorsContext.Provider value={fieldErrors}>
														<RegisterProformaFormContext.Provider value={registerProformaForm}>
															<SetProformaFormDirtyContext.Provider
																value={setProformaFormDirtyAction}
															>
																<ProformaFormIsDirtyContext.Provider value={proformaFormIsDirty}>
																	<JiraFieldDataContext.Provider value={jiraFieldData}>
																		<SetJiraFieldDataContext.Provider value={setJiraFieldData}>
																			<FormsContext.Provider value={forms}>
																				<SetFormsContext.Provider value={setForms}>
																					<ValidationFieldSetContext.Provider
																						value={validationFieldSet}
																					>
																						<SetValidationFieldSetContext.Provider
																							value={setValidationFieldSet}
																						>
																							{children}
																						</SetValidationFieldSetContext.Provider>
																					</ValidationFieldSetContext.Provider>
																				</SetFormsContext.Provider>
																			</FormsContext.Provider>
																		</SetJiraFieldDataContext.Provider>
																	</JiraFieldDataContext.Provider>
																</ProformaFormIsDirtyContext.Provider>
															</SetProformaFormDirtyContext.Provider>
														</RegisterProformaFormContext.Provider>
													</FieldErrorsContext.Provider>
												</BlockFormSubmissionContext.Provider>
											</KnowledgeBaseContext.Provider>
										</ReporterAccountIdContext.Provider>
									</ProformaTemplateFormContext.Provider>
								</SetMediaConfigContext.Provider>
							</MediaUploadConfigContext.Provider>
						</ProjectIdContext.Provider>
					</ServiceDeskIdContext.Provider>
				</RequestTypeIdContext.Provider>
			</PortalIdContext.Provider>
		</LocaleContext.Provider>
	);
};

export const useLocale = () => useContext(LocaleContext);
export const usePortalId = () => useContext(PortalIdContext);
export const useRequestTypeId = () => useContext(RequestTypeIdContext);
export const useServiceDeskId = () => useContext(ServiceDeskIdContext);
export const useProjectId = () => useContext(ProjectIdContext);
export const useMediaUploadConfig = () => useContext(MediaUploadConfigContext);
export const useSetMediaConfig = () => useContext(SetMediaConfigContext);
export const useProformaTemplateForm = () => useContext(ProformaTemplateFormContext);
export const useReporterAccountId = () => useContext(ReporterAccountIdContext);
export const useKnowledgeBase = () => useContext(KnowledgeBaseContext);
export const useBlockFormSubmission = () => useContext(BlockFormSubmissionContext);
export const useFieldErrors = () => useContext(FieldErrorsContext);
export const useRegisterProformaForm = () => useContext(RegisterProformaFormContext);
export const useSetProformaFormDirty = () => useContext(SetProformaFormDirtyContext);
export const useProformaFormIsDirty = () => useContext(ProformaFormIsDirtyContext);
export const useJiraFieldData = () => useContext(JiraFieldDataContext);
export const useSetJiraFieldData = () => useContext(SetJiraFieldDataContext);
export const useForms = () => useContext(FormsContext);
export const useSetForms = () => useContext(SetFormsContext);
export const useValidationFieldSet = () => useContext(ValidationFieldSetContext);
export const useSetValidationFieldSet = () => useContext(SetValidationFieldSetContext);
